/* Admin B2B section — Inquiries, Partners, Categories, Products, Revenue.
 *
 * Each subsection is a separate component but they all share an inline modal
 * pattern (create/edit) so the file stays self-contained and reuses the same
 * /api/admin/b2b/* endpoints.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Briefcase, Mail, Phone, Globe, Pencil, Trash2, Plus, X, Save, Search,
  Tag, Layers, Building2, Star, AlertCircle, BadgeIndianRupee, Package2,
  ShieldCheck, Pill, Sparkles, FileEdit, ShoppingBag, Truck, PackageCheck, ArrowRight,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/lib/toast";

const KINDS = [
  { value: "pharmaceutical", label: "Pharmaceuticals", icon: Pill },
  { value: "nutraceutical", label: "Nutraceuticals", icon: Sparkles },
];

const STATUSES = [
  { value: "new", label: "New", color: "#185FA5" },
  { value: "contacted", label: "Contacted", color: "#b5710d" },
  { value: "negotiating", label: "Negotiating", color: "#6938cb" },
  { value: "converted", label: "Converted", color: "#16704b" },
  { value: "closed", label: "Closed", color: "#475770" },
];

const fmtMoney = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtDay = (iso) => {
  try { return new Date(iso).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return ""; }
};

/* ===================== Inquiries ===================== */
export function B2BInquiries() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);   // expanded inquiry
  const [editNotes, setEditNotes] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/b2b/inquiries", { params: { status, q } });
      setItems(data.inquiries || []);
      setCounts(data.counts || {});
    } catch { setItems([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [status, q]);  // eslint-disable-line

  const setStatusOf = async (id, st) => {
    try {
      await api.patch(`/admin/b2b/inquiries/${id}`, { status: st });
      load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const saveNotes = async (id) => {
    try {
      await api.patch(`/admin/b2b/inquiries/${id}`, { notes: editNotes });
      toast("Notes saved");
      load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const removeOne = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/b2b/inquiries/${id}`);
    load();
  };

  return (
    <div data-testid="b2b-inquiries">
      <div className="b2b-adm-head">
        <div>
          <h2 className="h" style={{ margin: 0 }}><Mail size={20} style={{ verticalAlign: "-3px", marginRight: 8 }} />B2B Inquiries</h2>
          <p className="sub">Brand partnership requests from the public /b2b page.</p>
        </div>
        <div className="b2b-counts">
          {STATUSES.map((s) => (
            <span key={s.value} className="b2b-count-pill" style={{ "--c": s.color }}>
              <i /> {s.label}<b>{counts[s.value] || 0}</b>
            </span>
          ))}
        </div>
      </div>

      <div className="b2b-filterbar">
        <div className="patient-search-bar" style={{ maxWidth: 480, margin: 0 }}>
          <Search size={15} className="patient-search-ic" />
          <input className="patient-search-input" placeholder="Search company, name, email, phone…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="b2b-inquiry-search" />
        </div>
        <div className="b2b-status-filter" data-testid="b2b-status-filter">
          <button className={`b2b-fpill ${status === "" ? "on" : ""}`} onClick={() => setStatus("")}>All</button>
          {STATUSES.map((s) => (
            <button key={s.value} className={`b2b-fpill ${status === s.value ? "on" : ""}`}
              onClick={() => setStatus(s.value)} data-testid={`b2b-filter-${s.value}`}>{s.label}</button>
          ))}
        </div>
      </div>

      <div className="b2b-list" data-testid="b2b-inquiries-list">
        {busy && <p className="sub" style={{ padding: 12 }}>Loading…</p>}
        {!busy && items.length === 0 && <p className="b2b-empty">No inquiries yet.</p>}
        {!busy && items.map((it) => {
          const st = STATUSES.find((s) => s.value === it.status) || STATUSES[0];
          const isOpen = open === it.id;
          return (
            <div key={it.id} className={`b2b-card ${isOpen ? "open" : ""}`} data-testid={`b2b-inquiry-${it.id}`}>
              <button className="b2b-card-row" onClick={() => { setOpen(isOpen ? null : it.id); setEditNotes(it.notes || ""); }}>
                <span className="b2b-card-avatar"><Building2 size={16} /></span>
                <div className="b2b-card-meta">
                  <div className="b2b-card-top">
                    <b>{it.company}</b>
                    <span className="b2b-status" style={{ "--c": st.color }}>{st.label}</span>
                    <small className="b2b-card-kind">{it.interest === "general" ? "General" : it.interest}</small>
                    <time>{fmtDay(it.created_at)}</time>
                  </div>
                  <small>{it.name} · {it.email} · {it.phone}</small>
                  <p className="b2b-card-msg">{it.message}</p>
                </div>
              </button>
              {isOpen && (
                <div className="b2b-card-expand">
                  <div className="b2b-expand-grid">
                    <label>Status
                      <select value={it.status} onChange={(e) => setStatusOf(it.id, e.target.value)} data-testid={`b2b-status-${it.id}`}>
                        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </label>
                    {it.website && <label>Website<a href={it.website} target="_blank" rel="noreferrer" className="b2b-link">{it.website}</a></label>}
                    <label>Email<a href={`mailto:${it.email}`} className="b2b-link">{it.email}</a></label>
                    <label>Phone<a href={`tel:${it.phone}`} className="b2b-link">{it.phone}</a></label>
                  </div>
                  <label>Internal notes
                    <textarea rows={3} value={editNotes} onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Next steps, owner, follow-up date…" data-testid={`b2b-notes-${it.id}`} />
                  </label>
                  <div className="b2b-expand-actions">
                    <button className="btn btn-ink sm" onClick={() => saveNotes(it.id)} data-testid={`b2b-save-${it.id}`}><Save size={14} /> Save notes</button>
                    <button className="btn btn-ghost sm danger" onClick={() => removeOne(it.id)} data-testid={`b2b-delete-${it.id}`}><Trash2 size={14} /> Delete</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============== Generic CRUD modal helpers =============== */
function Modal({ title, onClose, children, wide }) {
  return (
    <div className="b2b-modal-bg" onClick={onClose}>
      <div className={`b2b-modal ${wide ? "wide" : ""}`} onClick={(e) => e.stopPropagation()} data-testid="b2b-modal">
        <header><b>{title}</b><button onClick={onClose} className="b2b-x" data-testid="b2b-modal-close"><X size={18} /></button></header>
        <div className="b2b-modal-body">{children}</div>
      </div>
    </div>
  );
}

/* ===================== Partners ===================== */
const EMPTY_PARTNER = { name: "", contact_name: "", email: "", phone: "", website: "", category: "", notes: "", active: true };

export function B2BPartners() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);  // null=closed, {id?, ...} otherwise
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/b2b/partners");
      setItems(data.partners || []);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...EMPTY_PARTNER, ...editing };
      if (editing.id) await api.patch(`/admin/b2b/partners/${editing.id}`, payload);
      else await api.post("/admin/b2b/partners", payload);
      setEditing(null); load();
      toast("Partner saved");
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete partner?")) return;
    await api.delete(`/admin/b2b/partners/${id}`);
    load();
  };

  return (
    <div data-testid="b2b-partners">
      <div className="b2b-adm-head">
        <div>
          <h2 className="h" style={{ margin: 0 }}><Briefcase size={20} style={{ verticalAlign: "-3px", marginRight: 8 }} />Partners</h2>
          <p className="sub">Brands collaborating with us — sources of products on the public storefronts.</p>
        </div>
        <button className="btn btn-ink" onClick={() => setEditing({ ...EMPTY_PARTNER })} data-testid="b2b-partner-new"><Plus size={15} /> New partner</button>
      </div>

      <div className="b2b-list">
        {items.length === 0 && <p className="b2b-empty">No partners yet. Add one to start listing their products.</p>}
        {items.map((p) => (
          <div key={p.id} className="b2b-card static" data-testid={`b2b-partner-${p.id}`}>
            <span className="b2b-card-avatar partner"><Briefcase size={16} /></span>
            <div className="b2b-card-meta">
              <div className="b2b-card-top">
                <b>{p.name}</b>
                {p.category && <small className="b2b-card-kind">{p.category}</small>}
                {!p.active && <span className="b2b-status" style={{ "--c": "#94a3b8" }}>Inactive</span>}
              </div>
              <small>
                {p.contact_name && <>{p.contact_name} · </>}
                {p.email && <a href={`mailto:${p.email}`} className="b2b-link">{p.email}</a>}
                {p.phone && <> · <a href={`tel:${p.phone}`} className="b2b-link">{p.phone}</a></>}
                {p.website && <> · <a href={p.website} target="_blank" rel="noreferrer" className="b2b-link">site</a></>}
              </small>
              {p.notes && <p className="b2b-card-msg">{p.notes}</p>}
            </div>
            <div className="b2b-card-actions">
              <button className="ib-iconbtn" onClick={() => setEditing(p)} data-testid={`b2b-partner-edit-${p.id}`}><Pencil size={14} /></button>
              <button className="ib-iconbtn danger" onClick={() => remove(p.id)} data-testid={`b2b-partner-del-${p.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit partner" : "New partner"} onClose={() => setEditing(null)}>
          <div className="b2b-form-grid">
            <label>Brand / company name *
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="b2b-partner-name" /></label>
            <label>Category
              <input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="Pharma OEM, Nutraceutical, Diagnostics…" /></label>
            <label>Contact person
              <input value={editing.contact_name} onChange={(e) => setEditing({ ...editing, contact_name: e.target.value })} /></label>
            <label>Email
              <input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></label>
            <label>Phone
              <input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></label>
            <label>Website
              <input value={editing.website} onChange={(e) => setEditing({ ...editing, website: e.target.value })} /></label>
            <label className="full">Notes
              <textarea rows={3} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></label>
            <label className="row"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
          </div>
          <div className="b2b-modal-foot">
            <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn btn-ink" onClick={save} disabled={busy || !editing.name.trim()} data-testid="b2b-partner-save"><Save size={14} /> Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ===================== Categories ===================== */
const EMPTY_CAT = { kind: "pharmaceutical", name: "", slug: "", description: "", order: 0, active: true };

export function B2BCategories() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("pharmaceutical");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/b2b/categories", { params: { kind } }); setItems(data.categories || []); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, [kind]);  // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...EMPTY_CAT, ...editing };
      if (editing.id) await api.patch(`/admin/b2b/categories/${editing.id}`, payload);
      else await api.post("/admin/b2b/categories", payload);
      setEditing(null); load();
      toast("Category saved");
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete category? Products in it will be uncategorised.")) return;
    await api.delete(`/admin/b2b/categories/${id}`);
    load();
  };

  return (
    <div data-testid="b2b-categories">
      <div className="b2b-adm-head">
        <div>
          <h2 className="h" style={{ margin: 0 }}><Layers size={20} style={{ verticalAlign: "-3px", marginRight: 8 }} />Categories</h2>
          <p className="sub">Group products on the public storefronts (pharmacy / nutraceutical).</p>
        </div>
        <button className="btn btn-ink" onClick={() => setEditing({ ...EMPTY_CAT, kind })} data-testid="b2b-cat-new"><Plus size={15} /> New category</button>
      </div>

      <div className="b2b-kind-tabs">
        {KINDS.map((k) => (
          <button key={k.value} className={`b2b-kindtab ${kind === k.value ? "on" : ""}`}
            onClick={() => setKind(k.value)} data-testid={`b2b-kind-${k.value}`}><k.icon size={14} /> {k.label}</button>
        ))}
      </div>

      <div className="b2b-list">
        {items.length === 0 && <p className="b2b-empty">No categories yet for {kind}.</p>}
        {items.map((c) => (
          <div key={c.id} className="b2b-card static" data-testid={`b2b-cat-${c.id}`}>
            <span className="b2b-card-avatar cat"><Tag size={16} /></span>
            <div className="b2b-card-meta">
              <div className="b2b-card-top">
                <b>{c.name}</b>
                <small className="b2b-card-kind">/{c.slug}</small>
                {!c.active && <span className="b2b-status" style={{ "--c": "#94a3b8" }}>Inactive</span>}
              </div>
              {c.description && <p className="b2b-card-msg">{c.description}</p>}
            </div>
            <div className="b2b-card-actions">
              <button className="ib-iconbtn" onClick={() => setEditing(c)}><Pencil size={14} /></button>
              <button className="ib-iconbtn danger" onClick={() => remove(c.id)}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit category" : "New category"} onClose={() => setEditing(null)}>
          <div className="b2b-form-grid">
            <label>Storefront *
              <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} data-testid="b2b-cat-kind">
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>
            <label>Name *
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="b2b-cat-name" />
            </label>
            <label>Slug
              <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto-generated from name" />
            </label>
            <label>Display order
              <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} />
            </label>
            <label className="full">Description
              <textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </label>
            <label className="row"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
          </div>
          <div className="b2b-modal-foot">
            <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn btn-ink" onClick={save} disabled={busy || !editing.name.trim()} data-testid="b2b-cat-save"><Save size={14} /> Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ===================== Products ===================== */
const EMPTY_PROD = {
  kind: "pharmaceutical", name: "", tagline: "", description: "",
  category_id: "", partner_id: "", sku: "", price: 0, mrp: 0,
  image_url: "", in_stock: true, featured: false, active: true, rx_required: false,
};

export function B2BProducts() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("pharmaceutical");
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [partners, setPartners] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/b2b/products", { params: { kind, q } }); setItems(data.products || []); }
    catch { setItems([]); }
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [kind, q]);  // eslint-disable-line
  useEffect(() => {
    api.get("/admin/b2b/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
    api.get("/admin/b2b/partners").then((r) => setPartners(r.data.partners || [])).catch(() => {});
  }, []);

  const catsForKind = useMemo(() => cats.filter((c) => c.kind === (editing?.kind || kind)), [cats, kind, editing]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...EMPTY_PROD, ...editing,
        price: Number(editing.price || 0), mrp: Number(editing.mrp || 0) };
      if (editing.id) await api.patch(`/admin/b2b/products/${editing.id}`, payload);
      else await api.post("/admin/b2b/products", payload);
      setEditing(null); load();
      toast("Product saved");
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete product?")) return;
    await api.delete(`/admin/b2b/products/${id}`);
    load();
  };

  return (
    <div data-testid="b2b-products">
      <div className="b2b-adm-head">
        <div>
          <h2 className="h" style={{ margin: 0 }}><Package2 size={20} style={{ verticalAlign: "-3px", marginRight: 8 }} />Products</h2>
          <p className="sub">Manage the catalogue shown on the public Pharmaceuticals and Nutraceuticals pages.</p>
        </div>
        <button className="btn btn-ink" onClick={() => setEditing({ ...EMPTY_PROD, kind })} data-testid="b2b-prod-new"><Plus size={15} /> New product</button>
      </div>

      <div className="b2b-filterbar">
        <div className="b2b-kind-tabs">
          {KINDS.map((k) => (
            <button key={k.value} className={`b2b-kindtab ${kind === k.value ? "on" : ""}`}
              onClick={() => setKind(k.value)} data-testid={`b2b-prod-kind-${k.value}`}><k.icon size={14} /> {k.label}</button>
          ))}
        </div>
        <div className="patient-search-bar" style={{ maxWidth: 360, margin: 0 }}>
          <Search size={15} className="patient-search-ic" />
          <input className="patient-search-input" placeholder="Search by name, SKU…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="b2b-prod-grid">
        {items.length === 0 && <p className="b2b-empty">No products in this storefront yet.</p>}
        {items.map((p) => (
          <div key={p.id} className="b2b-prod-card" data-testid={`b2b-prod-${p.id}`}>
            <div className="b2b-prod-img">
              {p.image_url ? <img src={p.image_url} alt={p.name} loading="lazy" />
                : <div className="b2b-prod-imgph"><Package2 size={28} /></div>}
              {p.featured && <span className="b2b-tag-feat"><Star size={10} fill="currentColor" /></span>}
            </div>
            <div className="b2b-prod-meta">
              <small>{p.category_name || "Uncategorised"}{p.rx_required ? " · Rx" : ""}</small>
              <b>{p.name}</b>
              {p.tagline && <span className="b2b-prod-tagline">{p.tagline}</span>}
              <div className="b2b-prod-foot">
                <span className="b2b-prod-price">
                  {fmtMoney(p.price)}
                  {p.mrp > p.price && <small> {fmtMoney(p.mrp)}</small>}
                </span>
                {p.partner_name && <small className="b2b-prod-partner"><ShieldCheck size={11} /> {p.partner_name}</small>}
              </div>
              {!p.active && <span className="b2b-status" style={{ "--c": "#94a3b8" }}>Inactive</span>}
            </div>
            <div className="b2b-prod-actions">
              <button className="ib-iconbtn" onClick={() => setEditing(p)} data-testid={`b2b-prod-edit-${p.id}`}><Pencil size={14} /></button>
              <button className="ib-iconbtn danger" onClick={() => remove(p.id)} data-testid={`b2b-prod-del-${p.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit product" : "New product"} onClose={() => setEditing(null)} wide>
          <div className="b2b-form-grid wide">
            <label>Storefront *
              <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value, category_id: "" })}>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>
            <label>Name *
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="b2b-prod-name" />
            </label>
            <label>SKU
              <input value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} />
            </label>
            <label>Tagline
              <input value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} placeholder="One-line description" />
            </label>
            <label>Category
              <select value={editing.category_id} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })}>
                <option value="">— uncategorised —</option>
                {catsForKind.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label>Brand partner
              <select value={editing.partner_id} onChange={(e) => setEditing({ ...editing, partner_id: e.target.value })}>
                <option value="">— direct —</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label>Price (₹)
              <input type="number" min="0" step="1" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="b2b-prod-price" />
            </label>
            <label>MRP (₹)
              <input type="number" min="0" step="1" value={editing.mrp} onChange={(e) => setEditing({ ...editing, mrp: e.target.value })} />
            </label>
            <label className="full">Image URL
              <input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="https://…" />
            </label>
            <label className="full">Description
              <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </label>
            <div className="b2b-checks">
              <label className="row"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
              <label className="row"><input type="checkbox" checked={!!editing.in_stock} onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })} /> In stock</label>
              <label className="row"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured</label>
              {editing.kind === "pharmaceutical" && (
                <label className="row"><input type="checkbox" checked={!!editing.rx_required} onChange={(e) => setEditing({ ...editing, rx_required: e.target.checked })} /> Prescription required</label>
              )}
            </div>
          </div>
          <div className="b2b-modal-foot">
            <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn btn-ink" onClick={save} disabled={busy || !editing.name.trim()} data-testid="b2b-prod-save"><Save size={14} /> Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ===================== Revenue (also injected into Business → Revenue) ===================== */
export function B2BRevenue() {
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get("/admin/b2b/revenue").then((x) => setR(x.data)).catch(() => setR(null));
  }, []);
  if (!r) return <p className="lead">Loading…</p>;
  return (
    <div data-testid="b2b-revenue">
      <div className="b2b-adm-head">
        <div>
          <h2 className="h" style={{ margin: 0 }}><BadgeIndianRupee size={20} style={{ verticalAlign: "-3px", marginRight: 8 }} />B2B Revenue</h2>
          <p className="sub">Aggregate from partner orders — also surfaced under <b>Business → Revenue</b>.</p>
        </div>
      </div>

      <div className="b2b-kpis" data-testid="b2b-kpis">
        <div className="b2b-kpi"><small>Total B2B revenue</small><b>{fmtMoney(r.revenue_total)}</b></div>
        <div className="b2b-kpi"><small>Last 30 days</small><b>{fmtMoney(r.revenue_last_30d)}</b></div>
        <div className="b2b-kpi"><small>Orders</small><b>{r.orders_count}</b></div>
        <div className="b2b-kpi"><small>Active products</small><b>{r.products}</b></div>
        <div className="b2b-kpi"><small>Partners</small><b>{r.partners}</b></div>
        <div className="b2b-kpi accent"><small>New inquiries</small><b>{r.new_inquiries}</b></div>
      </div>

      <div className="b2b-rev-split">
        <div className="b2b-rev-card">
          <h3>By storefront</h3>
          {Object.entries(r.by_kind || {}).map(([k, v]) => (
            <div key={k} className="b2b-rev-row"><span>{k === "pharmaceutical" ? "Pharmaceuticals" : "Nutraceuticals"}</span><b>{fmtMoney(v)}</b></div>
          ))}
        </div>
        <div className="b2b-rev-card">
          <h3>Top partners</h3>
          {Object.keys(r.by_partner || {}).length === 0 && <p className="sub" style={{ margin: 0 }}>No partner sales yet.</p>}
          {Object.entries(r.by_partner || {}).map(([k, v]) => (
            <div key={k} className="b2b-rev-row"><span>{k}</span><b>{fmtMoney(v)}</b></div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===================== Editable page content ===================== */
export function B2BPageContent() {
  const [kind, setKind] = useState("b2b");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/public/storefront/content", { params: { kind } }); setData(r.data.data); }
    catch { setData({}); }
  };
  useEffect(() => { load(); }, [kind]);  // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/storefront/content/${kind}`, { data }); toast("Saved"); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const set = (k, v) => setData({ ...data, [k]: v });
  const setList = (k, idx, field, v) => {
    const arr = [...(data[k] || [])];
    arr[idx] = { ...arr[idx], [field]: v };
    setData({ ...data, [k]: arr });
  };
  const addItem = (k, tmpl) => setData({ ...data, [k]: [...(data[k] || []), tmpl] });
  const delItem = (k, idx) => {
    const arr = [...(data[k] || [])]; arr.splice(idx, 1); setData({ ...data, [k]: arr });
  };

  return (
    <div data-testid="b2b-page-content">
      <div className="b2b-adm-head">
        <div>
          <h2 className="h" style={{ margin: 0 }}><FileEdit size={20} style={{ verticalAlign: "-3px", marginRight: 8 }} />B2B &amp; Storefront content</h2>
          <p className="sub">Edit the public hero copy of <b>/b2b</b>, <b>/pharmaceuticals</b> and <b>/nutraceuticals</b>.</p>
        </div>
        <button className="btn btn-ink" onClick={save} disabled={busy || !data} data-testid="b2b-content-save"><Save size={14} /> Save</button>
      </div>

      <div className="b2b-kind-tabs" style={{ marginBottom: 18 }}>
        <button className={`b2b-kindtab ${kind === "b2b" ? "on" : ""}`} onClick={() => setKind("b2b")} data-testid="b2b-content-kind-b2b">B2B page</button>
        <button className={`b2b-kindtab ${kind === "pharmaceutical" ? "on" : ""}`} onClick={() => setKind("pharmaceutical")} data-testid="b2b-content-kind-pharma">Pharmaceuticals</button>
        <button className={`b2b-kindtab ${kind === "nutraceutical" ? "on" : ""}`} onClick={() => setKind("nutraceutical")} data-testid="b2b-content-kind-nutra">Nutraceuticals</button>
      </div>

      {!data ? <p className="lead">Loading…</p> : (
        <div className="b2b-form-grid wide">
          <label>Eyebrow<input value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></label>
          <label className="full">Title<input value={data.title || ""} onChange={(e) => set("title", e.target.value)} /></label>
          <label className="full">Subtitle<textarea rows={2} value={data.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} /></label>
          {kind === "b2b" && <label>CTA button<input value={data.cta || ""} onChange={(e) => set("cta", e.target.value)} /></label>}

          {/* simple lists */}
          {kind !== "b2b" && (
            <div className="full">
              <label>Info bullets</label>
              {(data.info_lines || []).map((l, i) => (
                <div className="b2b-list-row" key={i}>
                  <input value={l} onChange={(e) => {
                    const arr = [...(data.info_lines || [])]; arr[i] = e.target.value; setData({ ...data, info_lines: arr });
                  }} />
                  <button className="ib-iconbtn danger" onClick={() => delItem("info_lines", i)}><Trash2 size={13} /></button>
                </div>
              ))}
              <button className="btn btn-ghost sm" onClick={() => addItem("info_lines", "New info bullet")}><Plus size={13} /> Add bullet</button>
            </div>
          )}

          {kind === "b2b" && (
            <>
              <div className="full">
                <label>Value props (cards on the page)</label>
                {(data.value_props || []).map((v, i) => (
                  <div className="b2b-list-block" key={i}>
                    <input placeholder="Title" value={v.title || ""} onChange={(e) => setList("value_props", i, "title", e.target.value)} />
                    <textarea rows={2} placeholder="Body" value={v.body || ""} onChange={(e) => setList("value_props", i, "body", e.target.value)} />
                    <button className="ib-iconbtn danger" onClick={() => delItem("value_props", i)}><Trash2 size={13} /></button>
                  </div>
                ))}
                <button className="btn btn-ghost sm" onClick={() => addItem("value_props", { title: "New value prop", body: "Describe it briefly." })}><Plus size={13} /> Add card</button>
              </div>

              <div className="full">
                <label>How it works</label>
                {(data.how_it_works || []).map((v, i) => (
                  <div className="b2b-list-block" key={i}>
                    <input placeholder="Step title" value={v.title || ""} onChange={(e) => setList("how_it_works", i, "title", e.target.value)} />
                    <textarea rows={2} placeholder="Body" value={v.body || ""} onChange={(e) => setList("how_it_works", i, "body", e.target.value)} />
                    <button className="ib-iconbtn danger" onClick={() => delItem("how_it_works", i)}><Trash2 size={13} /></button>
                  </div>
                ))}
                <button className="btn btn-ghost sm" onClick={() => addItem("how_it_works", { title: "Next step", body: "What happens next." })}><Plus size={13} /> Add step</button>
              </div>

              <div className="full">
                <label>FAQ</label>
                {(data.faq || []).map((f, i) => (
                  <div className="b2b-list-block" key={i}>
                    <input placeholder="Question" value={f.q || ""} onChange={(e) => setList("faq", i, "q", e.target.value)} />
                    <textarea rows={2} placeholder="Answer" value={f.a || ""} onChange={(e) => setList("faq", i, "a", e.target.value)} />
                    <button className="ib-iconbtn danger" onClick={() => delItem("faq", i)}><Trash2 size={13} /></button>
                  </div>
                ))}
                <button className="btn btn-ghost sm" onClick={() => addItem("faq", { q: "New question", a: "Answer." })}><Plus size={13} /> Add Q&A</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ===================== Compact tile for Business → Revenue ===================== */
export function B2BRevenueTile() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/b2b/revenue").then((x) => setR(x.data)).catch(() => setR(null)); }, []);
  if (!r) return null;
  return (
    <div className="panel" data-testid="b2b-business-tile" style={{ marginBottom: 16 }}>
      <h3 style={{ marginBottom: 4 }}><Briefcase size={17} style={{ verticalAlign: "-3px", marginRight: 7 }} />B2B revenue</h3>
      <p className="sub" style={{ marginBottom: 14 }}>
        Sales from brand-partner products on the public storefronts. Manage via
        the <b>B2B</b> section in the sidebar.
      </p>
      <div className="b2b-kpis" style={{ marginBottom: 0 }}>
        <div className="b2b-kpi"><small>Total B2B revenue</small><b>{fmtMoney(r.revenue_total)}</b></div>
        <div className="b2b-kpi"><small>Last 30 days</small><b>{fmtMoney(r.revenue_last_30d)}</b></div>
        <div className="b2b-kpi"><small>Orders</small><b>{r.orders_count}</b></div>
        <div className="b2b-kpi"><small>Active partners</small><b>{r.partners}</b></div>
      </div>
    </div>
  );
}

/* ===================== Orders ===================== */
const ORDER_STATUSES = [
  { value: "pending_payment", label: "Pending payment", color: "#b5710d" },
  { value: "paid", label: "Paid", color: "#185FA5" },
  { value: "packed", label: "Packed", color: "#6938cb" },
  { value: "shipped", label: "Shipped", color: "#3DAA82" },
  { value: "delivered", label: "Delivered", color: "#16704b" },
  { value: "cancelled", label: "Cancelled", color: "#94a3b8" },
  { value: "refunded", label: "Refunded", color: "#c03a2b" },
];

export function B2BOrders() {
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [nextStatus, setNextStatus] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/b2b/orders", { params: { status, q } });
      setOrders(data.orders || []);
      setCounts(data.counts || {});
    } catch { setOrders([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [status, q]);  // eslint-disable-line

  const updateStatus = async (oid) => {
    try {
      await api.patch(`/admin/b2b/orders/${oid}`, { status: nextStatus, note });
      toast("Order updated");
      setNote(""); setNextStatus("");
      load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="b2b-orders">
      <div className="b2b-adm-head">
        <div>
          <h2 className="h" style={{ margin: 0 }}><ShoppingBag size={20} style={{ verticalAlign: "-3px", marginRight: 8 }} />Orders</h2>
          <p className="sub">Real B2B orders from patients & doctors. Update status through the fulfilment flow.</p>
        </div>
        <div className="b2b-counts">
          {ORDER_STATUSES.map((s) => (
            <span key={s.value} className="b2b-count-pill" style={{ "--c": s.color }}>
              <i /> {s.label}<b>{counts[s.value] || 0}</b>
            </span>
          ))}
        </div>
      </div>

      <div className="b2b-filterbar">
        <div className="patient-search-bar" style={{ maxWidth: 480, margin: 0 }}>
          <Search size={15} className="patient-search-ic" />
          <input className="patient-search-input" placeholder="Search by name, email, phone or product…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="b2b-order-search" />
        </div>
        <div className="b2b-status-filter">
          <button className={`b2b-fpill ${status === "" ? "on" : ""}`} onClick={() => setStatus("")}>All</button>
          {ORDER_STATUSES.map((s) => (
            <button key={s.value} className={`b2b-fpill ${status === s.value ? "on" : ""}`}
              onClick={() => setStatus(s.value)} data-testid={`b2b-order-filter-${s.value}`}>{s.label}</button>
          ))}
        </div>
      </div>

      <div className="b2b-list">
        {busy && <p className="sub" style={{ padding: 12 }}>Loading…</p>}
        {!busy && orders.length === 0 && <p className="b2b-empty">No orders yet.</p>}
        {!busy && orders.map((o) => {
          const st = ORDER_STATUSES.find((s) => s.value === o.status) || ORDER_STATUSES[0];
          const isOpen = open === o.id;
          return (
            <div key={o.id} className={`b2b-card ${isOpen ? "open" : ""}`} data-testid={`b2b-order-${o.id}`}>
              <button className="b2b-card-row" onClick={() => { setOpen(isOpen ? null : o.id); setNextStatus(o.status); setNote(o.admin_note || ""); }}>
                <span className="b2b-card-avatar"><ShoppingBag size={16} /></span>
                <div className="b2b-card-meta">
                  <div className="b2b-card-top">
                    <b>#{o.id.slice(-8).toUpperCase()}</b>
                    <span className="b2b-status" style={{ "--c": st.color }}>{st.label}</span>
                    <small className="b2b-card-kind">{o.kind}</small>
                    <small className="b2b-card-kind">{fmtMoney(o.total_amount)} · {o.items?.length || 0} items</small>
                    <time>{fmtDay(o.created_at)}</time>
                  </div>
                  <small>{o.user_name} ({o.user_role}) · {o.user_email} · {o.shipping?.phone}</small>
                  <p className="b2b-card-msg">
                    {(o.items || []).map((it) => `${it.qty}× ${it.name}`).join(" · ")}
                  </p>
                </div>
              </button>
              {isOpen && (
                <div className="b2b-card-expand">
                  <div className="b2b-order-detail">
                    <div>
                      <h4>Items</h4>
                      <ul className="b2b-order-items">
                        {(o.items || []).map((it, i) => (
                          <li key={i}>
                            <b>{it.name}</b>
                            <small>Qty {it.qty} · {fmtMoney(it.price)} each {it.rx_required && "· Rx"}</small>
                            <span>{fmtMoney(it.line_total)}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="b2b-order-totals">
                        <div><span>Subtotal</span><b>{fmtMoney(o.subtotal)}</b></div>
                        <div><span>Shipping</span><b>{fmtMoney(o.shipping_fee)}</b></div>
                        <div className="total"><span>Total</span><b>{fmtMoney(o.total_amount)}</b></div>
                      </div>
                    </div>
                    <div>
                      <h4>Ship to</h4>
                      <p className="b2b-order-addr">
                        <b>{o.shipping?.full_name}</b><br />
                        {o.shipping?.line1}{o.shipping?.line2 && <>, {o.shipping.line2}</>}<br />
                        {o.shipping?.city}, {o.shipping?.state} — {o.shipping?.pincode}<br />
                        <small>{o.shipping?.phone}</small>
                      </p>
                      {o.prescription_ids?.length > 0 && (
                        <p className="b2b-order-rx"><ShieldCheck size={12} /> Rx attached: {o.prescription_ids.length}</p>
                      )}
                      <h4 style={{ marginTop: 14 }}>Update status</h4>
                      <div className="b2b-status-update">
                        <select value={nextStatus} onChange={(e) => setNextStatus(e.target.value)} data-testid={`b2b-order-next-${o.id}`}>
                          {ORDER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (optional)" />
                        <button className="btn btn-ink sm" onClick={() => updateStatus(o.id)} disabled={!nextStatus || nextStatus === o.status} data-testid={`b2b-order-save-${o.id}`}>
                          <ArrowRight size={13} /> Update
                        </button>
                      </div>
                      {o.admin_note && <p className="ck-muted small">Last note: {o.admin_note}</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

